import {
  handleSubmitMultilingualForm,
  useCreateExpertise,
  useGetExpertiseFormData,
  useUpdateExpertise,
} from "@/api/cms";
import { CmsPageAppearanceConfig } from "@/components/cms/theme/CmsPageAppearanceConfig";
import ExpertiseForm from "@/components/cms/ExpertiseForm.tsx";
import { Switch } from "@/components/form-items/AbstractForm";
import { DoubleMultilingualField } from "@/components/form-items/MultilingualField";
import { useAuth } from "@/context/auth/AuthContext";
import { useCmsWebsiteLanguages } from "@/hooks/use-cms-website-languages";
import { useToast } from "@/hooks/use-toast";
import {
  constructCmsLocaleDict,
  multilingualFieldSchema,
  SupportedLocale,
} from "@/i18n";
import CMSLayout from "@/layout/CMSLayout.tsx";
import {
  cmsPageLocaleBaseSchemaShape,
  expertiseContentSchema,
  withPageNameRequiredWhenPublished,
} from "@/lib/cms/cms-types";
import {
  buildCmsPagePatchPayload,
  pageThemeFromDto,
} from "@/lib/cms/page-theme";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { FormFieldVisibilityProvider } from "@/context/FormFieldVisibilityContext";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const expertiseFormSchema = multilingualFieldSchema(
  withPageNameRequiredWhenPublished(
    cmsPageLocaleBaseSchemaShape.merge(
      expertiseContentSchema.omit({
        id: true,
      }),
    ),
  ).optional(),
);

type ExpertiseFormData = z.infer<typeof expertiseFormSchema>;

const ExpertiseCMS = () => {
  const { t } = useTranslation();
  const { websiteLanguages } = useCmsWebsiteLanguages();
  const { instanceData } = useAuth();

  const create = useCreateExpertise();
  const update = useUpdateExpertise();

  const { data: expertiseData } = useGetExpertiseFormData();

  const hideFormFields = instanceData?.hideNewArtworkAutheticationForm === true;

  const defaultValues: ExpertiseFormData = useMemo(
    () =>
      constructCmsLocaleDict((lang) => {
        const exp = expertiseData?.[lang];
        return {
          published: exp?.published ?? false,
          pageName: exp?.pageName ?? "",
          ...pageThemeFromDto(exp),
          title: exp?.content.title ?? "",
          content: exp?.content.content ?? "",
          formDownloadCTA: exp?.content.formDownloadCTA ?? "",
          documentUrl: exp?.content.documentUrl ?? "",
        };
      }, websiteLanguages),
    [expertiseData, websiteLanguages],
  );

  const form = useForm<ExpertiseFormData>({
    resolver: zodResolver(expertiseFormSchema),
    defaultValues,
  });

  const { reset } = form;

  const hasInitialized = useRef(false);
  useEffect(() => {
    if (
      !hasInitialized.current &&
      expertiseData != null &&
      defaultValues &&
      Object.keys(defaultValues).length > 0
    ) {
      reset(defaultValues);
      hasInitialized.current = true;
    }
  }, [reset, defaultValues, expertiseData]);

  const { toast } = useToast();

  const onSubmit = (submittedData: ExpertiseFormData) => {
    if (instanceData?.CMS?.id == null) {
      console.error("Tried to submit but there is no CMS");
      return;
    }

    Object.keys(form.formState.dirtyFields).forEach((key) => {
      const localeData = submittedData[key as SupportedLocale];
      const baseData = expertiseData?.[key as SupportedLocale];
      if (!localeData) return;

      const mutation = baseData?.content.id ? update : create;
      const pageNameStr = localeData.pageName ?? "";
      mutation.mutate(
        {
          id: baseData?.content.id ?? "",
          lang: key as SupportedLocale,
          data: buildCmsPagePatchPayload({
            pageName: pageNameStr,
            published: localeData.published,
            pageBackgroundColorId: localeData.pageBackgroundColorId,
            pageTextColorMode: localeData.pageTextColorMode,
            pageTextColorId: localeData.pageTextColorId,
            pageFontFamily: localeData.pageFontFamily,
            pageFontVariant: localeData.pageFontVariant,
            content: {
              title: localeData.title,
              content: localeData.content,
              ...(hideFormFields
                ? {
                    formDownloadCTA: localeData.formDownloadCTA,
                    documentUrl: localeData.documentUrl,
                  }
                : {}),
            },
          }),
        },
        {
          onSuccess: () => {
            toast({
              title: t("success"),
              description: t("homepageSuccessMessage"),
              duration: 4000,
              variant: "success",
            });
          },
          onError: () => {
            toast({
              title: t("error"),
              description: t("homepageErrorMessage"),
              variant: "error",
              duration: 4000,
            });
          },
        },
      );
    });
  };

  return (
    <CMSLayout pageName={t("cmsTabExpertise")} formIds={["expertise"]}>
      <FormProvider {...form}>
        <FormFieldVisibilityProvider formKey="cms-expertise">
        <CmsPageAppearanceConfig formId="expertise" />
        <form
          onSubmit={handleSubmitMultilingualForm(form, expertiseData, onSubmit)}
          className="flex w-full flex-col gap-6"
          id="expertise"
        >
          <DoubleMultilingualField
            className="w-full"
            corner={(lang) => (
              <Switch dispLabel={false} name={`${lang}.published`} />
            )}
            render={(lang) => <ExpertiseForm language={lang} />}
          />
        </form>
              </FormFieldVisibilityProvider>
      </FormProvider>
    </CMSLayout>
  );
};

export default ExpertiseCMS;
